import React, { useState } from 'react';
import { Plan, PlanCreate } from '../types';

interface PlanFormProps {
  initialData?: Plan | null;
  onSubmit: (data: PlanCreate) => void;
  onCancel: () => void;
}

const PlanForm: React.FC<PlanFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<PlanCreate>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    valid_from: initialData?.valid_from 
      ? new Date(initialData.valid_from).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    valid_to: initialData?.valid_to 
      ? new Date(initialData.valid_to).toISOString().split('T')[0]
      : '',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required';
    }

    if (formData.valid_to && formData.valid_to <= formData.valid_from) {
      newErrors.valid_to = 'Valid to date must be after valid from date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Convert dates to ISO format for the backend
      const submitData = {
        ...formData,
        valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : '',
        valid_to: formData.valid_to ? new Date(formData.valid_to).toISOString() : undefined
      };
      
      // Remove empty valid_to if not provided
      if (!submitData.valid_to) {
        delete submitData.valid_to;
      }
      
      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'error' : ''}`}
          placeholder="Enter plan name"
        />
        {errors.name && <div className="form-error">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Enter plan description"
        />
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Valid From *</label>
            <input
              type="date"
              name="valid_from"
              value={formData.valid_from}
              onChange={handleChange}
              className={`form-control ${errors.valid_from ? 'error' : ''}`}
            />
            {errors.valid_from && <div className="form-error">{errors.valid_from}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Valid To</label>
            <input
              type="date"
              name="valid_to"
              value={formData.valid_to}
              onChange={handleChange}
              className={`form-control ${errors.valid_to ? 'error' : ''}`}
            />
            {errors.valid_to && <div className="form-error">{errors.valid_to}</div>}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
          />
          Active
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default PlanForm;